import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  SalesRoute, DeliveryRoute, DeliveryStop, RouteEfficiencyMetric,
  CreateDeliveryRouteInput, CreateStopInput, DeliveryRouteStatus,
  listSalesRoutes, listDeliveryRoutes, getDeliveryRoute, getMyDriverRoute,
  listRouteMetrics, assignDeliveryDriver,
  createDeliveryRoute, addStopToRoute, updateDeliveryRouteStatus,
} from '../services/routesApi';

interface RoutesState {
  salesRoutes: SalesRoute[];
  deliveryRoutes: DeliveryRoute[];
  myDriverRoute: DeliveryRoute | null;
  metrics: RouteEfficiencyMetric[];
  loading: boolean;
  error: string | null;
  fetchSalesRoutes: () => Promise<void>;
  fetchDeliveryRoutes: (filters?: { status?: string; date?: string }) => Promise<void>;
  fetchDeliveryRoute: (id: number | string) => Promise<DeliveryRoute>;
  fetchMyDriverRoute: () => Promise<void>;
  fetchMetrics: (filters?: { from?: string; to?: string; routeType?: string }) => Promise<void>;
  assignDriver: (routeId: number | string, driverId: number) => Promise<DeliveryRoute>;
  createRoute: (input: CreateDeliveryRouteInput) => Promise<DeliveryRoute>;
  addStop: (routeId: number | string, input: CreateStopInput) => Promise<DeliveryStop>;
  updateRouteStatus: (routeId: number | string, status: DeliveryRouteStatus) => Promise<DeliveryRoute>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useRoutesStore = create<RoutesState>()(
  devtools(
    (set) => ({
      salesRoutes: [],
      deliveryRoutes: [],
      myDriverRoute: null,
      metrics: [],
      loading: false,
      error: null,
      fetchSalesRoutes: async () => {
        set({ loading: true, error: null });
        try { set({ salesRoutes: await listSalesRoutes(), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchDeliveryRoutes: async (filters) => {
        set({ loading: true, error: null });
        try { set({ deliveryRoutes: await listDeliveryRoutes(filters), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchDeliveryRoute: async (id) => {
        const route = await getDeliveryRoute(id);
        set((s) => ({
          deliveryRoutes: s.deliveryRoutes.map((r) => (String(r.id) === String(id) ? route : r)),
        }));
        return route;
      },
      fetchMyDriverRoute: async () => {
        set({ loading: true, error: null });
        try { set({ myDriverRoute: await getMyDriverRoute(), loading: false }); }
        catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      fetchMetrics: async (filters) => {
        try { set({ metrics: await listRouteMetrics(filters) }); }
        catch (err) { set({ error: toErr(err) }); }
      },
      assignDriver: async (routeId, driverId) => {
        const updated = await assignDeliveryDriver(routeId, driverId);
        set((s) => ({
          deliveryRoutes: s.deliveryRoutes.map((r) => (r.id === updated.id ? updated : r)),
        }));
        return updated;
      },
      createRoute: async (input) => {
        const created = await createDeliveryRoute(input);
        set((s) => ({ deliveryRoutes: [created, ...s.deliveryRoutes] }));
        return created;
      },
      addStop: async (routeId, input) => {
        return await addStopToRoute(routeId, input);
      },
      updateRouteStatus: async (routeId, status) => {
        const updated = await updateDeliveryRouteStatus(routeId, status);
        set((s) => ({
          deliveryRoutes: s.deliveryRoutes.map((r) => (String(r.id) === String(routeId) ? { ...r, ...updated } : r)),
          myDriverRoute: s.myDriverRoute && String(s.myDriverRoute.id) === String(routeId)
            ? { ...s.myDriverRoute, ...updated }
            : s.myDriverRoute,
        }));
        return updated;
      },
    }),
    { name: 'routes-store' }
  )
);
